/* eslint-disable @typescript-eslint/no-explicit-any */

interface INavigatorExtended extends Navigator {
  msLaunchUri: (uri: string, successCallback?: () => void, noHandlerCallback?: () => void) => void;
}

// Allowed protocol schemes for launching external applications
const ALLOWED_PROTOCOLS = ['msdefender:'];

/**
 * Validates that a URL uses an allowed protocol scheme.
 * This prevents client-side URL redirection vulnerabilities.
 * @param url The URL to validate
 * @returns true if the URL is safe to use, false otherwise
 */
export function isValidProtocolUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim().toLowerCase();

  // Check if URL starts with an allowed protocol and is followed by valid characters
  return ALLOWED_PROTOCOLS.some((protocol) => {
    const protocolPattern = new RegExp(`^${protocol}(?:[/?#].*)?$`);
    return protocolPattern.test(trimmedUrl);
  });
}

function createHiddenIframe(target: HTMLElement, url: string) {
  const iframe = document.createElement('iframe');
  iframe.src = url;
  iframe.id = 'hiddenIframe';
  iframe.style.display = 'none';
  target.appendChild(iframe);

  return iframe;
}

function triggerMSLaunch(url: string, successCb: () => void, failCb: () => void) {
  // Validate URL before using it to prevent unvalidated redirects
  if (!isValidProtocolUrl(url)) {
    failCb();
    return;
  }

  const timeout = window.setTimeout(() => {
    // if msLaunchUri promise is not resolved or rejected within 2 seconds, consider it failed.
    failCb();
  }, 2000);

  (window.navigator as INavigatorExtended).msLaunchUri(
    url,
    () => {
      window.clearTimeout(timeout);
      successCb();
    },
    () => {
      window.clearTimeout(timeout);
      failCb();
    }
  );
}

function openUsingIframe(url: string, successCb: () => void, failCb: () => void) {
  // Validate URL before using it to prevent unvalidated redirects
  if (!isValidProtocolUrl(url)) {
    failCb();
    return;
  }

  let iframe: HTMLIFrameElement | null = document.querySelector('#hiddenIframe');
  let timeout: number | undefined;

  const blurHandler = () => {
    // if app successfully launches, browser window will lose focus
    clearTimeout(timeout);
    window.removeEventListener('blur', blurHandler);
    successCb();
  };
  const timeoutHandler = () => {
    window.removeEventListener('blur', blurHandler);
    // if timeout expires before window loses focus, that means the app failed to launch
    failCb();
  };
  window.addEventListener('blur', blurHandler);
  timeout = window.setTimeout(timeoutHandler, 3000);

  if (!iframe) {
    iframe = createHiddenIframe(document.body, 'about:blank');
  }
  if (iframe.contentWindow) {
    iframe.contentWindow.location.href = url;
  }
}

export function launchProtocolUrl(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window.navigator as INavigatorExtended).msLaunchUri) {
      triggerMSLaunch(url, resolve, reject);
    } else {
      openUsingIframe(url, resolve, reject);
    }
  });
}
